import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SEO } from "@/components/SEO";
import { WEBSITE_SCHEMA, HOME_LOCAL_BUSINESS_SCHEMA, HOME_FAQ_SCHEMA } from "@/lib/schemas";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { ArrowRight, Users, Target, Heart, Shield, Briefcase, Quote } from "lucide-react";
import { latestArticleCards, homeGalleryPhotos } from "@/data/homeContent";
import { testimonials } from "@/data/testimonials";

import heroAvif480 from "@/assets/hero-home-480.avif";
import heroAvif768 from "@/assets/hero-home-768.avif";
import heroAvif1280 from "@/assets/hero-home-1280.avif";
import heroAvif1600 from "@/assets/hero-home-1600.avif";
import heroAvif1920 from "@/assets/hero-home-1920.avif";
import heroWebp480 from "@/assets/hero-home-480.webp";
import heroWebp768 from "@/assets/hero-home-768.webp";
import heroWebp1280 from "@/assets/hero-home-1280.webp";
import heroWebp1600 from "@/assets/hero-home-1600.webp";
import heroWebp1920 from "@/assets/hero-home-1920.webp";
import heroTeamAvif480 from "@/assets/hero-team-480.avif";
import heroTeamAvif768 from "@/assets/hero-team-768.avif";
import heroTeamAvif1280 from "@/assets/hero-team-1280.avif";
import heroTeamAvif1600 from "@/assets/hero-team-1600.avif";
import heroTeamAvif1920 from "@/assets/hero-team-1920.avif";
import heroTeamWebp480 from "@/assets/hero-team-480.webp";
import heroTeamWebp768 from "@/assets/hero-team-768.webp";
import heroTeamWebp1280 from "@/assets/hero-team-1280.webp";
import heroTeamWebp1600 from "@/assets/hero-team-1600.webp";
import heroTeamWebp1920 from "@/assets/hero-team-1920.webp";
import childrenImage from "@/assets/children-upload-optimized.webp";
import childrenImage680 from "@/assets/children-upload-680.webp";
import teensImage from "@/assets/teens-upload-optimized.webp";
import teensImage680 from "@/assets/teens-upload-680.webp";
import adultsImage from "@/assets/adults-upload-optimized.webp";
import adultsImage680 from "@/assets/adults-upload-680.webp";
import beltCeremonyImage from "@/assets/belt-ceremony.webp";
import vitaImage from "@/assets/vita-upload-optimized.webp";

const Index = () => {
  const latestArticles = latestArticleCards.filter(
    (c) => !c.publishedAt || new Date(c.publishedAt).getTime() <= Date.now()
  );
  const latestGalleryPhotos = homeGalleryPhotos;
  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;
    const id = window.setInterval(() => setHeroSlide((s) => (s === 0 ? 1 : 0)), 5000);
    return () => window.clearInterval(id);
  }, []);

  // Mitigation for hosting SPA-fallback flash: if the URL on first mount is not "/",
  // the server served root index.html (this Index page) for a deeper route. React
  // Router will navigate to the right page in a moment — render a blank placeholder
  // so users don't see the home flashing before the article/page they requested.
  if (typeof window !== "undefined" && window.location.pathname !== "/") {
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }


  return (
    <Layout>
      <SEO
        title="Taekwondo a Roma per Bambini e Adulti | Prova Gratuita"
        description="Scuola di Taekwondo a Roma tra Portuense e Monteverde: corsi per bambini dai 4 anni, ragazzi e adulti con Maestri FITA. Prenota la lezione di prova gratuita."
        canonical="https://taekwondomattei.com/"
        ogImage="https://taekwondomattei.com/og-home.jpg"
        schemaJsonLd={[WEBSITE_SCHEMA, HOME_LOCAL_BUSINESS_SCHEMA, HOME_FAQ_SCHEMA]}
      />
      <Helmet>
        {/* Preload responsive LCP image — AVIF preferred, browser picks the right variant */}
        <link
          rel="preload"
          as="image"
          type="image/avif"
          href={heroAvif768}
          imageSrcSet={`${heroAvif480} 480w, ${heroAvif768} 768w, ${heroAvif1280} 1280w, ${heroAvif1920} 1920w`}
          imageSizes="100vw"
          fetchPriority="high"
        />
        <meta name="theme-color" content="#000000" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <picture
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${heroSlide === 0 ? "opacity-100" : "opacity-0"}`}
          >
            <source
              type="image/avif"
              srcSet={`${heroAvif480} 480w, ${heroAvif768} 768w, ${heroAvif1280} 1280w, ${heroAvif1600} 1600w, ${heroAvif1920} 1920w`}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={`${heroWebp480} 480w, ${heroWebp768} 768w, ${heroWebp1280} 1280w, ${heroWebp1600} 1600w, ${heroWebp1920} 1920w`}
              sizes="100vw"
            />
            <img
              src={heroWebp1280}
              alt="Allenamento di Taekwondo nella nostra palestra"
              width={1600}
              height={1067}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
          </picture>
          <picture
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${heroSlide === 1 ? "opacity-100" : "opacity-0"}`}
          >
            <source
              type="image/avif"
              srcSet={`${heroTeamAvif480} 480w, ${heroTeamAvif768} 768w, ${heroTeamAvif1280} 1280w, ${heroTeamAvif1600} 1600w, ${heroTeamAvif1920} 1920w`}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={`${heroTeamWebp480} 480w, ${heroTeamWebp768} 768w, ${heroTeamWebp1280} 1280w, ${heroTeamWebp1600} 1600w, ${heroTeamWebp1920} 1920w`}
              sizes="100vw"
            />
            <img
              src={heroTeamWebp1280}
              alt="La squadra del Taekwondo Mattei al Lazio Combat 2026 di Fondi"
              width={1600}
              height={1067}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className="w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative z-10 container-wide mx-auto px-4 md:px-8 text-center">
          <div className="max-w-4xl mx-auto space-y-6 animate-fade-up">
            <h1 className="font-display font-bold text-gold leading-tight">
              <span className="block font-body font-medium tracking-widest uppercase text-sm md:text-base text-gold/80 mb-4">
                Scuola di Taekwondo a Roma
              </span>
              <span className="block text-4xl md:text-6xl lg:text-7xl">
                Crescere forti,<br />dentro e fuori
              </span>
            </h1>
            <p className="font-body text-lg md:text-xl lg:text-2xl text-gold/90 max-w-2xl mx-auto">
              Corsi di Taekwondo a Roma per bambini, ragazzi e adulti — dalla prima cintura all'agonismo
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link to="/lezione-di-prova" className="btn-accent text-lg">
                Prenota una lezione di prova
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/percorsi" className="btn-outline-light text-lg">
                Scopri i percorsi
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-gold/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Iscrizioni Stagione 2026/2027 */}
      <section className="pt-16 md:pt-24 pb-6 md:pb-8 bg-background border-b border-gold/10">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto rounded-2xl border border-gold/30 bg-secondary/40 p-8 md:p-12 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs font-body uppercase tracking-wider mb-4">
                Iscrizioni aperte
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
                Stagione 2026/2027: iscriviti all'Accademia
              </h2>
              <p className="font-body text-muted-foreground mb-6 max-w-xl">
                Corsi di Taekwondo da settembre 2026. Lun/Mer/Ven per bambini, ragazzi e adulti,
                percorso agonistico e non. Sede: scuola Mazzacurati, Roma.
              </p>
              <Link to="/stagione-2026-2027" className="btn-accent inline-flex items-center">
                Scopri e iscriviti
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="hidden md:flex flex-col items-center justify-center p-6 rounded-xl bg-background border border-gold/20 min-w-[180px]">
              <div className="font-display text-5xl font-bold text-gold leading-none">26/27</div>
              <div className="font-body text-xs uppercase tracking-widest text-muted-foreground mt-2">
                Stagione
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A chi ci rivolgiamo */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="A chi ci rivolgiamo"
            subtitle="Un percorso su misura per ogni età e obiettivo"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Bambini 6-10 anni",
                description: "Crescita motoria, disciplina e divertimento in un ambiente sicuro e stimolante.",
                image: childrenImage,
                imageSmall: childrenImage680,
                link: "/percorsi#bambini"
              },
              {
                title: "Ragazzi 11-15 anni",
                description: "Sviluppo fisico, autostima e preparazione atletica per l'adolescenza.",
                image: teensImage,
                imageSmall: teensImage680,
                link: "/percorsi#ragazzi"
              },
              {
                title: "Adulti",
                description: "Forma fisica, tecnica e benessere mentale per chi inizia o vuole riprendere.",
                image: adultsImage,
                imageSmall: adultsImage680,
                link: "/percorsi#adulti"
              }
            ].map((item, index) => (
              <Link
                key={item.title}
                to={item.link}
                className="group card-elevated overflow-hidden border border-border"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                 <div className="relative aspect-[4/3] overflow-hidden">
                   <img
                     src={item.imageSmall}
                     srcSet={`${item.imageSmall} 800w, ${item.image} 1184w`}
                     sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                     alt={item.title}
                     width={1184}
                     height={864}
                     loading="lazy"
                     decoding="async"
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                   />
                   <div className="absolute inset-0 hero-overlay-soft pointer-events-none" />
                 </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground mb-4">
                    {item.description}
                  </p>
                  <span className="font-body text-sm font-medium text-gold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Scopri di più <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Il nostro metodo */}
      <section className="section-padding gradient-black">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Il nostro metodo"
            subtitle="Un approccio educativo che mette al centro la persona"
            className="[&_h2]:text-gold [&_p]:text-gold/70"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Shield,
                title: "Sicurezza",
                description: "Allenamenti strutturati con attenzione alla prevenzione degli infortuni e al rispetto dei tempi di ogni allievo."
              },
              {
                icon: Target,
                title: "Disciplina",
                description: "Costanza, rispetto delle regole, concentrazione e autocontrollo: valori che il Taekwondo trasmette attraverso la pratica quotidiana."
              },
              {
                icon: Heart,
                title: "Crescita personale",
                description: "Ogni cintura rappresenta un traguardo che rafforza autostima e determinazione."
              },
              {
                icon: Briefcase,
                title: "Professionalità",
                description: "Maestri qualificati che trasmettono tradizione, competenza tecnica e cultura sportiva con metodi moderni e aggiornati."
              }
            ].map((item, index) => (
              <div
                key={item.title}
                className="p-6 rounded-xl bg-gold/5 backdrop-blur-sm border border-gold/20"
              >
                <div className="w-12 h-12 rounded-lg bg-gold flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gold mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-gold/70 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Cosa dicono i genitori */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Cosa dicono i genitori"
            subtitle="Le esperienze di chi ha scelto la nostra Academy"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((testimonial) => (
              <div
                key={testimonial.id}
                className="card-elevated p-8 border border-border relative"
              >
                <Quote className="absolute top-6 right-6 h-8 w-8 text-gold/20" />
                <div className="space-y-4">
                  <p className="font-body text-foreground leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="pt-4 border-t border-border">
                    <p className="font-display font-semibold text-foreground">
                      {testimonial.name}
                    </p>
                    <p className="font-body text-sm text-muted-foreground">
                      {testimonial.role}
                    </p>
                    {testimonial.isFormerAthlete && (
                      <span className="inline-block mt-2 px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                        Ex atleta dell'Academy
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview Vita in Palestra */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Vita in palestra"
            subtitle="Momenti di allenamento, eventi e crescita condivisa"
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <p className="font-body text-lg text-muted-foreground leading-relaxed">
                Durante l'anno la nostra palestra è animata da allenamenti ordinari, esami di cintura, 
                stage federali, gare e momenti formativi che scandiscono il percorso di ogni allievo.
              </p>
              <p className="font-body text-lg text-muted-foreground leading-relaxed">
                Dagli eventi interni alle competizioni, ogni attività è un'occasione per crescere 
                insieme, rafforzare il gruppo e vivere appieno i valori del Taekwondo.
              </p>
              <Link to="/vita-in-palestra" className="btn-primary inline-flex">
                Scopri la nostra vita in palestra
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            
             <div className="relative overflow-hidden rounded-2xl shadow-large">
              <img
                src={vitaImage}
                alt="Momento in palestra: allievi durante una lezione"
                width={1184}
                height={864}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 hero-overlay-soft pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gold rounded-full flex items-center justify-center shadow-gold">
                <span className="font-display text-2xl font-bold text-primary">+500</span>
              </div>
              <span className="absolute -bottom-10 right-0 font-body text-sm text-muted-foreground">
                allievi formati
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery grid preview */}
      <section className="pb-16 md:pb-24 bg-background">
        <div className="container-wide mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {latestGalleryPhotos.map((photo, index) => (
              <Link
                key={index}
                to="/vita-in-palestra"
                className="group relative aspect-[4/3] overflow-hidden rounded-xl"
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  width={800}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* Latest Articles */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <SectionHeader
              title="Dal nostro blog"
              subtitle="Consigli e approfondimenti sul Taekwondo"
              centered={false}
              className="mb-0"
            />
            <Link to="/blog" className="font-body text-sm font-medium text-gold inline-flex items-center gap-1 hover:gap-2 transition-all">
              Tutti gli articoli <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {latestArticles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group card-elevated border border-border p-6"
              >
                <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                  {article.category === "bambini" ? "Bambini" : 
                   article.category === "ragazzi" ? "Ragazzi" :
                   article.category === "adulti" ? "Adulti" :
                   article.category === "zona" ? "Zona" :
                   article.category === "agonisti" ? "Agonisti" : "Generale"}
                </span>
                <h3 className="font-display text-lg font-semibold text-foreground mt-4 group-hover:text-gold transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
                  {article.excerpt}
                </p>
                <span className="font-body text-sm font-medium text-gold inline-flex items-center gap-1 mt-4 group-hover:gap-2 transition-all">
                  Leggi <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Taekwondo vicino a te */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Taekwondo vicino a te"
            subtitle="La nostra palestra è al centro di Roma sud-ovest"
          />
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-body text-lg text-muted-foreground leading-relaxed mb-6">
              La nostra sede si trova in <strong className="text-foreground">Via Marino Mazzacurati 90</strong>, a pochi minuti da{" "}
              <Link to="/blog/taekwondo-portuense-roma" className="text-gold underline underline-offset-2">Portuense</Link>,{" "}
              <Link to="/blog/taekwondo-trullo-corviale-roma" className="text-gold underline underline-offset-2">Trullo</Link>,{" "}
              <Link to="/blog/taekwondo-trullo-corviale-roma" className="text-gold underline underline-offset-2">Corviale</Link>,{" "}
              <Link to="/blog/taekwondo-bravetta-casetta-mattei" className="text-gold underline underline-offset-2">Bravetta</Link>,{" "}
              <Link to="/blog/taekwondo-bravetta-casetta-mattei" className="text-gold underline underline-offset-2">Casetta Mattei</Link>,{" "}
              <Link to="/blog/taekwondo-monteverde-casaletto-roma" className="text-gold underline underline-offset-2">Monteverde</Link> e{" "}
              <Link to="/blog/taekwondo-monteverde-casaletto-roma" className="text-gold underline underline-offset-2">Casaletto</Link>.
            </p>
            <p className="font-body text-muted-foreground leading-relaxed">
              Cerchi un corso di{" "}
              <Link to="/blog/accademia-arti-marziali-roma" className="text-gold underline underline-offset-2">arti marziali a Roma</Link>?
              Offriamo percorsi per{" "}
              <Link to="/percorsi#bambini" className="text-gold underline underline-offset-2">bambini</Link>,{" "}
              <Link to="/percorsi#ragazzi" className="text-gold underline underline-offset-2">ragazzi</Link> e{" "}
              <Link to="/percorsi#adulti" className="text-gold underline underline-offset-2">adulti</Link>,
              con la prima lezione sempre gratuita.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ locale — intento di ricerca "Taekwondo Roma" */}
      <section className="section-padding bg-secondary">
        <div className="container-narrow mx-auto">
          <SectionHeader
            title="Taekwondo a Roma: domande frequenti"
            subtitle="Le risposte alle domande che riceviamo più spesso"
          />
          <div className="space-y-4 mt-8">
            {[
              {
                q: "Dove praticare Taekwondo a Roma?",
                a: (
                  <>
                    La nostra palestra si trova in <strong className="text-foreground">Via Marino Mazzacurati 90</strong>,
                    zona Portuense/Monteverde, a pochi minuti da Bravetta, Corviale, Trullo, Casetta Mattei e Casaletto.
                    È una delle scuole di <Link to="/" className="text-gold underline underline-offset-2">Taekwondo a Roma</Link> più
                    storiche del quadrante sud-ovest.
                  </>
                ),
              },
              {
                q: "Quanto costa un corso di Taekwondo a Roma?",
                a: (
                  <>
                    A Roma il costo tipico comprende iscrizione annuale (30–80€), quota mensile (40–70€ per 2 lezioni a
                    settimana), dobok (25–40€) ed esami di cintura (20–40€). Trovi il dettaglio completo nella{" "}
                    <Link to="/blog/costi-taekwondo-roma" className="text-gold underline underline-offset-2">
                      guida ai costi del Taekwondo a Roma
                    </Link>. La prima lezione è gratuita.
                  </>
                ),
              },
              {
                q: "Da quale età si può iniziare?",
                a: (
                  <>
                    I corsi partono dai <strong className="text-foreground">5 anni</strong> per i bambini e proseguono con
                    ragazzi, adulti e percorso agonistico. Non esiste un'età massima:{" "}
                    <Link to="/blog/taekwondo-adulti-principianti-iniziare" className="text-gold underline underline-offset-2">
                      anche da adulti si può iniziare
                    </Link>.
                  </>
                ),
              },
              {
                q: "La lezione di prova è davvero gratuita?",
                a: (
                  <>
                    Sì. La prima lezione è <strong className="text-foreground">gratuita e senza impegno</strong>, sia per
                    bambini che per adulti.{" "}
                    <Link to="/lezione-di-prova" className="text-gold underline underline-offset-2">
                      Prenotala online
                    </Link>{" "}
                    e vieni a conoscerci.
                  </>
                ),
              },
              {
                q: "Serve attrezzatura per iniziare?",
                a: (
                  <>
                    No. Per la prova basta un abbigliamento comodo. Il dobok (divisa) si acquista dopo l'iscrizione;
                    le protezioni servono solo quando si inizia lo sparring.
                  </>
                ),
              },
            ].map((item, idx) => (
              <details
                key={idx}
                className="group rounded-xl border border-border bg-background p-6 open:shadow-medium transition-shadow"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-display text-lg md:text-xl font-semibold text-foreground pr-4">
                    {item.q}
                  </h3>
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center transition-transform group-open:rotate-45">
                    <span className="text-xl leading-none">+</span>
                  </span>
                </summary>
                <p className="font-body text-base text-muted-foreground leading-relaxed mt-4">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>



      {/* Final CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-6">
            Inizia il tuo percorso
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            La prima lezione è gratuita e senza impegno. Vieni a conoscerci e scopri 
            se il Taekwondo è lo sport giusto per te o per i tuoi figli.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
